/**
 * Component Loading Manager
 * 组件加载管理器 - 解决页面刷新时延迟加载组件未就绪的问题
 */

import store from '@/store'

class ComponentLoader {
  constructor() {
    this.loadingPromises = new Map()
    this.loadedComponents = new Set()
    this.criticalRoutes = new Set([
      '/cart',
      '/payment',
      '/checkout',
      '/product-detail'
    ])
  }

  /**
   * 注册组件加载承诺
   */
  registerLoadingPromise(name, promise) {
    this.loadingPromises.set(name, promise)
    promise.then(() => {
      this.loadedComponents.add(name)
      this.loadingPromises.delete(name)
    }).catch(error => {
      console.error(`Failed to load ${name}:`, error)
      this.loadingPromises.delete(name)
    })
  }

  /**
   * 等待特定组件加载完成
   */
  async waitForComponent(name) {
    if (this.loadedComponents.has(name)) {
      return true
    }

    const promise = this.loadingPromises.get(name)
    if (promise) {
      await promise
      return true
    }

    return false
  }

  /**
   * 等待多个组件加载完成
   */
  async waitForComponents(names) {
    const promises = names
      .filter(name => !this.loadedComponents.has(name))
      .map(name => this.waitForComponent(name))

    await Promise.all(promises)
  }

  /**
   * 检查是否为关键路由
   */
  isCriticalRoute(path) {
    return this.criticalRoutes.has(path) ||
           this.criticalRoutes.has('/' + path.split('/')[1])
  }

  /**
   * 根据路由预加载必要组件
   */
  async preloadForRoute(route) {
    const requiredComponents = this.getRequiredComponentsForRoute(route)

    if (requiredComponents.length > 0) {
      // 设置加载状态
      store.commit('SET_COMPONENTS_LOADING', true)

      try {
        await this.waitForComponents(requiredComponents)
        store.commit('SET_COMPONENTS_LOADING', false)
      } catch (error) {
        console.error('Failed to preload components:', error)
        store.commit('SET_COMPONENTS_LOADING', false)
        // 可以选择抛出错误或继续
      }
    }
  }

  /**
   * 获取路由所需的组件列表
   */
  getRequiredComponentsForRoute(route) {
    const path = route.path
    const components = []

    // 基础组件 - 所有页面都需要
    components.push('vant-base', 'global-base')

    // 根据路由判断需要的组件
    if (path.includes('cart')) {
      components.push('vant-form', 'global-popup', 'muse-ui')
    }

    if (path.includes('payment') || path.includes('checkout')) {
      components.push('vant-form', 'global-popup', 'element-ui', 'muse-ui')
    }

    // contact-details 页面需要 global-popup
    if (path.includes('contact-details') || path.includes('contact_details')) {
      components.push('global-popup')
    }

    if (path.includes('product')) {
      components.push('vant-tabs', 'global-popup')
    }

    if (path.includes('venue-home')) {
      components.push('vant-tabs')
    }

    // 表单相关页面需要 Element UI
    if (path.includes('login') || path.includes('register') || path.includes('form')) {
      components.push('element-ui')
    }

    // 可能需要 Muse UI 的页面
    if (path.includes('dialog') || path.includes('select')) {
      components.push('muse-ui')
    }

    return components
  }

  /**
   * 立即加载所有延迟组件
   */
  async forceLoadAllComponents() {
    const promises = Array.from(this.loadingPromises.values())
    await Promise.all(promises)
  }
}

// 创建单例
const componentLoader = new ComponentLoader()

// 导出实例和辅助函数
export default componentLoader

/**
 * 在路由守卫中使用
 */
export async function ensureComponentsLoaded(to, from, next) {
  // 如果是关键路由，确保组件已加载
  if (componentLoader.isCriticalRoute(to.path)) {
    try {
      await componentLoader.preloadForRoute(to)
    } catch (error) {
      console.error('Component preload failed:', error)
      // 可以选择显示错误页面或继续
    }
  }

  next()
}

/**
 * 组件加载装饰器
 */
export function withComponentLoader(componentName) {
  return function(target) {
    const originalMounted = target.mounted || target.created

    target.mounted = async function(...args) {
      // 等待必要组件加载
      await componentLoader.waitForComponent(componentName)

      // 调用原始方法
      if (originalMounted) {
        return originalMounted.apply(this, args)
      }
    }

    return target
  }
}

/**
 * Vue 插件
 */
export const ComponentLoaderPlugin = {
  install(Vue) {
    // 添加全局方法
    Vue.prototype.$waitForComponent = (name) => {
      return componentLoader.waitForComponent(name)
    }

    Vue.prototype.$waitForComponents = (names) => {
      return componentLoader.waitForComponents(names)
    }

    // 添加全局 mixin
    Vue.mixin({
      async beforeCreate() {
        // 对于某些关键组件，确保依赖已加载
        if (this.$options.requiredComponents) {
          await componentLoader.waitForComponents(this.$options.requiredComponents)
        }
      }
    })
  }
}
